type SiteHelperQueryState = 'starting' | 'started' | 'stopped';

interface IQueryRequestFilter {
    $type?: string;
    input?: string;
}
interface IQueryRequest {
    query: string;
    context: IQueryRequestContext;
}
interface IQuerySaveRequest {
    query: string;
    id: string;
    path: string;
    value: string;
}
interface ISiteHelperQueryConfig {
    perPage: number,
    extraTop: number,
    extraBottom: number,
    maxRefresh: number,
    autoRefresh: boolean
}
interface IQueryRequestContextOptions {
    waitForNonStale?: boolean;
}
interface IQueryRequestContext {
    from: number;
    limit: number;

    waitForNonStale?: boolean;

    filter: IQueryRequestFilter;
    orderBy?: string;
}
interface IQueryResponseStatistics {
    duration: number;
    lastWrite: number;
    totalResults: number;
}
interface IQueryResponse {
    results: any[];
    statistics: IQueryResponseStatistics;
}
interface ISiteHelperQuery
    extends IQueryElement {

    statisticsEvent: IQueryEventAsync<IQueryResponseStatistics>;
    templateEvent: IQueryEventAsync<ISiteHelperQueryTemplate>;
    clickEvent: IQueryEventAsync<QueryEventedModel<ISiteHelperQueryRequestItem>>;

    resize(): void;
    refresh(): Promise<void>;

    selectorUp(): void;
    selectorDown(): void;
    getSelector(): ISiteHelperQueryRequestItem | null;
    makeSelector(selector: ISiteHelperQueryRequestItem): void;

    getFilter(): IQueryRequestFilter | undefined;
    setFilter(filter: IQueryRequestFilter): Promise<void>;
    getOrderBy(): string | undefined;
    setOrderBy(orderBy: string): Promise<void>;
    setTemplate(template: string): Promise<void>;
    getTemplate(): string | undefined;

    getPageItems(): ISiteHelperQueryRequestItem[];

    itemMakeSelect(id: string): void;
    itemMakeUnselect(id: string): void;

    getMode(): SiteHelperQuery2dModes;
    setMode(mode: SiteHelperQuery2dModes): void;

    start(): void;
    stop(): void;
}
interface ISiteHelperQueryRequest
    extends IQueryElement {

}
interface ISiteHelperQueryRequestItem
    extends IQueryElement {
    
    makeActive(): void;
    makeInactive(): void;

    makeSelector(): void;
    makeUnselector(): void;

    isSelected(): boolean;
    makeSelect(): void;
    makeUnselect(): void;

    getResult(): any;
}
interface ISiteHelperQueryTemplate {
    name: string;
    type: SiteHelperQuery2dTypes;
}

class SiteHelperQueryInfo2d {
    active?: string;
    selector?: number;
    selected: { [key: string]: SiteHelperQueryRequestItem2d | null }
        = {};
}


type SiteHelperQuery2dTypes = 'list' | 'grid';
type SiteHelperQuery2dModes = 'view' | 'edit';

class SiteHelperQuery2dTemplate {
    public name: string;
    public type: SiteHelperQuery2dTypes;
    public template: QueryTemplate;
    public width: number;
    public height: number;
    public columns: string | null;

    constructor(name: string, type: SiteHelperQuery2dTypes, template: QueryTemplate, columns: string | null, width: number, height: number) {
        this.name = name;
        this.type = type;
        this.template = template;
        this.columns = columns;
        this.width = width;
        this.height = height;
    } 
}

class SiteHelperQuery2d
    extends QueryElement<HTMLElement>
    implements ISiteHelperQuery {

    private _refreshInterval?: number;
    private _refreshTimeout?: number;

    private _mode: SiteHelperQuery2dModes = "view";
    private _state: SiteHelperQueryState = "starting";
    private _templates: SiteHelperQuery2dTemplate[] = [];
    private _template: SiteHelperQuery2dTemplate 
        = new SiteHelperQuery2dTemplate("", "list", new QueryTemplate(""), null, 0, 0);
    private _height: number = 0;
    private _width: number = 0;
    private _scrollTop: number = 0;

    private readonly _info: SiteHelperQueryInfo2d
        = new SiteHelperQueryInfo2d();

    private readonly _query: IQueryInfo;
    private readonly _style: CSSStyleDeclaration;
    private readonly _columns: SiteHelperQueryColumns2d;
    private readonly _container: IQueryElement;
    private readonly _pages: SiteHelperQueryRequest2d[]
        = [];
    private readonly _statisticsEvent: QueryEventAsync<IQueryResponseStatistics>
        = new QueryEventAsync<IQueryResponseStatistics>();
    private readonly _clickEvent: QueryEventAsync<QueryEventedModel<ISiteHelperQueryRequestItem>>
        = new QueryEventAsync<QueryEventedModel<ISiteHelperQueryRequestItem>>();
    private readonly _templateEvent: QueryEventAsync<ISiteHelperQueryTemplate>
        = new QueryEventAsync<ISiteHelperQueryTemplate>();

    private _statistics?: IQueryResponseStatistics;

    private readonly _config = {
        perPage: 25,
        extraTop: 5,
        extraBottom: 5,

        maxRefresh: 120,
        autoRefresh: false
    };

    public get clickEvent() { return this._clickEvent.expose(); }
    public get statisticsEvent() { return this._statisticsEvent.expose(); }
    public get templateEvent() { return this._templateEvent.expose(); }

    constructor(
        elem: HTMLElement,
        query: IQueryInfo,
        config?: ISiteHelperQueryConfig) {

        super(elem);

        if (query === null)
            throw "Unknown query!";

        if (config !== undefined)
            this._config = config;

        this._columns = new SiteHelperQueryColumns2d(this)
            .appendTo(this);

        this._container = QueryElement.QueryElement("div")
            .appendTo(this);

        this._container.on("click", (e: MouseEvent) => {
            if (this._mode != "view")
                return;

            let position = this.position();
            let columnPosition = this._columns.position();
            let results = this.get();
            let columns = this.getColumns();
            if (position === null || results === null || columnPosition === null)
                return;

            let posY = e.pageY - position.top - columnPosition.height + results.scrollTop;
            let posX = e.pageX - position.left;

            let row = Math.floor(posY / this._template.height);
            let column = Math.floor(posX / (this._width / columns));
            let item = ((row * columns) + column);

            //console.log(`Item: ${item}; Row: ${row}; Column: ${column}`);

            let requester = Math.floor(item / this._config.perPage);
            let itemToGet = item % this._config.perPage;

            let resultItem = this._pages[requester].getResultItem(itemToGet);

            this._clickEvent.trigger(new QueryEventedModel(resultItem, e));
        });

        let style = this._container.style();
        if (style === undefined)
            throw "Couldn't access style attribute!";

        this._style = style;
        this._query = query;

        var f = throttle(() => this.render(), 250);
        this.on("scroll", () => {
            this._scrollTop = this.scrollTop();
            f();
        });
    }
    getMode() {
        return this._mode;
    }
    setMode(mode: SiteHelperQuery2dModes) {
        this.removeClass(`mode-${this._mode}`);
        this._mode = mode;
        this.addClass(`mode-${this._mode}`);

        if (this._mode == "edit")
            this.stop();
        else
            this.start();
    }
    resize() {
        if (this._state == "starting")
            return;
        
        let elem = this.get();
        if (elem === null)
            return 0;
        
        elem.scrollTop = this._scrollTop;
        this._height = elem.clientHeight;

        if (this._width != elem.clientWidth) {
            this._width = elem.clientWidth;

            var results = this._statistics?.totalResults ?? 0;
            this._style.height = (Math.ceil(results / this.getColumns()) * this._template.height) + "px";

            this.getPages().forEach((a) => a.resize());

            var selector = this.getSelector();
            if (selector != null)
                this.makeSelector(selector);
            
            console.log(`Resize: Width: ${this._width}; Height: ${this._height}`);
        } else {
            console.log(`Resize: Height: ${this._height}`);
        }
    }

    start() {
        if (this._state == "starting") {
            let firstChild = this.query(".templates").first();

            if (firstChild === null)
                throw "No template found!";

            // Get templates
            for (var elem of firstChild.children())
            {
                var templateElement = elem.firstChild()?.get() as HTMLElement;
                var templateElementColumns = 
                    elem.children().length == 2 ? elem.lastChild()?.get() as HTMLElement : null;
                var templateName = elem.getAttr("data-template") as string;
                var templateType = elem.getAttr("data-type") as SiteHelperQuery2dTypes;
                var templateHeight = templateElement.scrollHeight;
                var templateWidth = templateElement.scrollWidth;

                var template = new SiteHelperQuery2dTemplate(
                    templateName, 
                    templateType, 
                    new QueryTemplate(templateElement.outerHTML),
                    templateElementColumns?.outerHTML || null,
                    templateWidth,
                    templateHeight);
                
                this._templates.push(template);
            }
            
            firstChild.remove();

            if (this._templates.length == 0)
                throw "No templates found!";
            
            // Select template
            var isMobile = Site.isMobile();
            if (isMobile) {
                this.setTemplate(this._templates.filter(a => a.type == "list")[0].name);
            } else if (this._query.template) {
                this.setTemplate(this._query.template);
            } else {
                this.setTemplate(this._templates[0].name);
            }

            // Set started
            this._state = "started";
            
            this.resize();
            this.render();
        }
        else {
            // Set started
            this._state = "started";
        }
        this.checkRefreshTimer();
    }
    stop() {
        this._state = "stopped";
        this.clearRefreshTimer();
    }
    private clearRefreshTimer() {
        clearTimeout(this._refreshTimeout);
    }
    private checkRefreshTimer() {
        if (!this._config.autoRefresh 
            || this._state != "started")
            return;

        this._refreshInterval = Math.min(
            Math.max(this._refreshInterval || 0, 5),
            this._config.maxRefresh);

        console.log("Set a refresh timer to: " + this._refreshInterval);

        this.clearRefreshTimer();
        this._refreshTimeout = setTimeout(async () => {
            await this.refresh();
        }, this._refreshInterval * 1000);
    }
    async refresh() {
        // Need to check for undefined, test of _pages[x] will create a undef value
        for (let page of this._pages.filter(a => a !== undefined)) {
            await page.setExpired();
            if (page.isVisible())
                await page.request({
                    waitForNonStale: true
                });
        }
        
        this.checkRefreshTimer();
    }

    private request(from: number) {
        if (this._template == null)
            return null;
        
        // Add an elementRequest
        let request = new SiteHelperQueryRequest2d(this, this._template.template, {
            query: this._query.query,
            context: {
                from: from,
                limit: this._config.perPage,
                filter: this._query.filter || {},
                orderBy: this._query.orderBy
            }
        });
        
        // Find first element with top higher than this item
        let elementAfter = this._container.children<SiteHelperQueryRequest2d>()
            .filter(a => a.getOrder() > from)
            .first()
        
        // Append to container
        if (elementAfter == null)
            this._container.append(request);
        else
            this._container.insertBefore(request, elementAfter);
            
        return request;
    }
    setStatistics(statistics: IQueryResponseStatistics) {
        this._statistics = statistics;
        this._style.height = (Math.ceil(this._statistics.totalResults / this.getColumns()) * this._template.height) + "px";

        this._statisticsEvent.trigger(this._statistics);

        this._refreshInterval = Math.ceil(this._statistics.lastWrite / 10) * 5;

        // This will trigger it again, but will directly update the refreshInterval
        this.checkRefreshTimer();
    }
    getFilter() {
        return this._query.filter;
    }
    async setFilter(filter: IQueryRequestFilter) {
        this._query.filter = filter;
        await this.reloadResults();
    }
    getOrderBy() {
        return this._query.orderBy;
    }
    async setOrderBy(orderBy: string) {
        this._query.orderBy = orderBy;
        await this.reloadResults();
    }
    getTemplate() {
        return this._query.template;
    }
    async setTemplate(name: string) {
        var template = this._templates.filter(a => a.name == name).first();
        if (template != null) {
            this._template = template;
            this.clearPages();
            this._container.setAttr("class", "");
            this._container.addClass(template.type);
            this._container.addClass(`template-${template.name}`);

            this._columns.setContent(template.columns || "");
            this._columns.setAttr("class", "");
            this._columns.addClass(template.type);
            this._columns.addClass(`columns-${template.name}`);

            this._templateEvent.trigger({
                name: "",
                type: template.type
            });
            await this.reloadResults();
        }
    }
    private async reloadResults() {
        if (this._state == "starting")
            return;

        if (this._state == "stopped") {
            this.clearPages();
        } else {
            let request = this.request(0);
            if(request != null) {
                await request.request({});
                this.clearPages();
                this._pages[0] = request;
                this.render();
            }
        }
    }


    private clearPages() {
        // Scroll to top:
        let elem = this.get();
        if (elem)
            elem.scrollTop = 0;

        // Remove pages
        while (this._pages.length) {
            let page = this._pages.pop();
            page && page.remove();
        }
    }

    itemMakeSelect(id: string) {
        this._info.selected[id] = null;
    }
    itemMakeUnselect(id: string) {
        let item = this._info.selected[id];

        // Null or undefined
        if (item != null)
            item.makeUnselect();
        else
            delete this._info.selected[id];
    }
    getSelector() {
        return this._container.query("div:not(.hide) > .selector").first<ISiteHelperQueryRequestItem>();
    }
    selectorUp() {
        let prevElement: ISiteHelperQueryRequestItem | null | undefined;
        let element = this.getSelector();

        if (element) {
            let parent = element.parent<SiteHelperQueryRequest2d>();
            if (parent) {
                let results = parent.getResults();
                let index = parent.getResults().indexOf(element);
                if (index - 1 >= 0) {
                    prevElement = results[index - 1];
                }
                else {
                    let prevParent = parent.prev<SiteHelperQueryRequest2d>();
                    if (prevParent) {
                        prevElement = prevParent.getResults().last();
                    }
                }
            }
        } else {
            let elem = this.get();
            elem && (elem.scrollTop = 0);

            prevElement = this._pages[0].getResultItem(0);
            element = prevElement;
        }

        if (prevElement != null) {
            element.makeUnselector();
            this.makeSelector(prevElement);
        }
    }
    selectorDown() {
        let nextElement: ISiteHelperQueryRequestItem | null | undefined;
        let element = this.getSelector();


        if (element) {
            let parent = element.parent<SiteHelperQueryRequest2d>();
            if (parent) {
                let results = parent.getResults();
                let index = parent.getResults().indexOf(element);
                if (results.length > index + 1) {
                    nextElement = results[index + 1];
                }
                else {
                    let nextParent = parent.next<SiteHelperQueryRequest2d>();
                    if (nextParent) {
                        nextElement = nextParent.getResults().first();
                    }
                }
            }
        } else {
            let elem = this.get();
            elem && (elem.scrollTop = 0);

            nextElement = this._pages[0].getResultItem(0);
            element = nextElement;
        }

        if (nextElement != null) {
            element.makeUnselector();
            this.makeSelector(nextElement);
        }
    }

    makeSelector(selector: ISiteHelperQueryRequestItem) {
        selector.makeSelector();
        
        let elem = this.get();

        let parent = selector.parent<SiteHelperQueryRequest2d>();
        if (!elem || !parent)
            return;
        
        let columns = this.getColumns();
        let itemToSelect = parent.getResults()
            .indexOf(selector) + parent.getOrder();
        let itemToSelectPosition = Math.floor(itemToSelect / columns) * this._template.height;
        
        let scrollTop = this.scrollTop();
        var height = elem.clientHeight;

        console.log(`ScrollTop: ${scrollTop}; ItemsPosition: ${itemToSelectPosition}; Selected: ${itemToSelect}; Columns: ${columns}; ${height}`)

        if (itemToSelectPosition < scrollTop)
            elem.scrollTop = itemToSelectPosition;
        else if (itemToSelectPosition + this._template.height > (scrollTop + height))
            elem.scrollTop = itemToSelectPosition + this._template.height - height;
    }

    private getPages() {
        return this._container.children<SiteHelperQueryRequest2d>();
    }
    getPageItems() {
        let array : SiteHelperQueryRequestItem2d[] = [];
        for(var page of this.getPages())
        {
            array.push(...page.children<SiteHelperQueryRequestItem2d>());
        }
        return array;
    }

    getTemplateWidth() {
        return this._template.width;
    }
    getTemplateHeight() {
        return this._template.height;
    }
    getColumns() : number {
        return Math.floor(this._width / this._template.width) || 1;
    }
    getStatisticsResults() {
        return this._statistics?.totalResults ?? 0;
    }

    getInfo() {
        return this._info;
    }

    private scrollTop() {
        let elem = this.get();
        if (elem === null)
            return 0;

        return elem.scrollTop;
    }

    private _displaying: { from: number, to: number } = {
        from: 0,
        to: 0
    };
    private async render() {
        let elem = this.get();
        if (elem === null)
            return;
        
        let scrollTop = this.scrollTop();
        let columns = this.getColumns();

        // Calculate what to display:
        let firstRow = (scrollTop / this._template.height) - this._config.extraTop;
        if (firstRow < 0)
            firstRow = 0;

        let lastRow = ((scrollTop + this._height) / this._template.height) + this._config.extraBottom;
        if (this._statistics != null)
        {
            let lastCalculatedRow = Math.ceil((this._statistics.totalResults ?? 0) / columns);
            if (lastRow > lastCalculatedRow)
                lastRow = lastCalculatedRow;
        }

        let from = Math.floor(firstRow * columns / this._config.perPage);
        let to = Math.ceil(lastRow * columns / this._config.perPage);

        //console.log(`${scrollTop}; Columns: ${columns}; Results: ${this._statistics?.totalResults}; FirstRow: ${firstRow}; LastRow: ${lastRow}; FromPage: ${from}; ToPage: ${to}`);

        // Hide displaying.from to from
        for (let x = this._displaying.from; x < from; x++) {
            let toHide = this._pages[x];
            if (toHide !== undefined)
                toHide.hide();
        }
        // Hide to to displaying.to
        for (let x = to; x <= this._displaying.to; x++) {
            let toHide = this._pages[x];
            if (toHide !== undefined)
                toHide.hide();
        }

        this._displaying = {
            from: from,
            to: to
        };

        // Show / get pages
        for (let x = Math.max(from, this._displaying.from); x < Math.min(to, this._displaying.to); x++) {
            let result = this._pages[x];
            if (this._pages[x] === undefined) {
                let request = this.request(x * this._config.perPage);
                if (request != null) {
                    this._pages[x] = request;
                    await this._pages[x].request({});
                }    
            }
            else {
                result.show();

                if (result.isExpired())
                    await result.request({});
            }
        }
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);
        this.stop();
    }
}

class SiteHelperQueryColumns2d
    extends QueryElement<HTMLElement>
    implements ISiteHelperQueryRequest {

    private readonly _parent: SiteHelperQuery2d;
    private _columns: SiteHelperQueryColumn2d[] = [];

    constructor(parent: SiteHelperQuery2d) {
        super(document.createElement("div"));
        this._parent = parent;
    }

    setContent(html: string) {
        this.setHtml(html);
        this._columns = this.query(".sort").createAll<SiteHelperQueryColumn2d, HTMLElement>(
            (a) => new SiteHelperQueryColumn2d(a, this)
        );
    }
    setSort(name: string) {
        for(let column of this._columns) {
            column.setSort(name);
        }
        this._parent.setOrderBy(name);
    }
}
class SiteHelperQueryColumn2d
    extends QueryElement<HTMLElement>
    implements ISiteHelperQueryRequest {

    private _active : number = -1;
    private readonly _sorters: string[];
    private readonly _parent: SiteHelperQueryColumns2d;

    constructor(element: HTMLElement, parent: SiteHelperQueryColumns2d) {
        super(element);

        var sorters = this.getAttr("data-sort");
        if (sorters == null)
            throw "Sorters not found!";
        
        this._sorters = sorters.split(",");
        this._parent = parent;

        this.on("click", () => {
            this._active += 1;
            if (this._active > this._sorters.length)
                this._active = 0;

            this._parent.setSort(this._sorters[this._active]);
        });
    }
    setSort(name: string) {
        this._active = this._sorters.indexOf(name);

        this.setAttr("class", "sort");
        if (this._active > -1) {
            this.addClass(`sort-${this._active}`);
        }
    }
}

class SiteHelperQueryRequest2d
    extends QueryElement<HTMLElement>
    implements ISiteHelperQueryRequest {

    private readonly _template: QueryTemplate;
    private readonly _request: IQueryRequest;
    private readonly _parent: SiteHelperQuery2d;
    private _results: ISiteHelperQueryRequestItem[] = [];
    private _expired: boolean;
    private _startSpacer: SiteHelperQueryRequestSpacer2d;
    private _endSpacer: SiteHelperQueryRequestSpacer2d;
    private _style: CSSStyleDeclaration;

    constructor(parent: SiteHelperQuery2d, template: QueryTemplate, request: IQueryRequest) {
        super(document.createElement("div"));

        let style = this.style();
        if (style === undefined)
            throw "Unable to get style!";

        this._style = style;
        this._template = template;
        this._request = request;
        this._parent = parent;
        this._expired = false;
        
        this._startSpacer = new SiteHelperQueryRequestSpacer2d(this._parent, "start").appendTo(this);
        this._endSpacer = new SiteHelperQueryRequestSpacer2d(this._parent, "end").appendTo(this);

        this.addClass("page");
    }

    resize() {
        let columns = this._parent.getColumns();
        let startSpacer = this._request.context.from % columns;
        
        let contextTo = this._request.context.from + this._request.context.limit;
        if (contextTo > this._parent.getStatisticsResults())
            contextTo = this._parent.getStatisticsResults();
        
        let endSpacer = columns - (contextTo % columns);
        if (endSpacer == columns)
            endSpacer = 0;

        this._startSpacer.resize(startSpacer);
        this._endSpacer.resize(endSpacer);

        let skipColumns = Math.floor(this._request.context.from / columns);
        this._style.top = `${skipColumns * this._parent.getTemplateHeight()}px`;

        //console.log(`Columns: ${columns}; TemplateWidth: ${this._parent.getTemplateWidth()}; Top: ${this._style.top}; StartSpacer: ${startSpacer}; EndSpacer: ${endSpacer}`);
    }

    async request(options: IQueryRequestContextOptions) {
        this._expired = false;
        console.log(`Requesting: ${this._request.context.from}`);
        
        // Fill the extra options
        this._request.context.waitForNonStale = options.waitForNonStale;

        let request = new QueryRequest('Api/Query', {
            contentType: 'json',
            method: 'post',
            data: JSON.stringify(this._request)
        });

        let response = await request.request();
        let queryResponse = await response.json() as IQueryResponse;

        // Clear the results
        this._results.forEach(a => a.remove());
        this._results = [];

        // Fill them
        for (let x = 0; x < queryResponse.results.length; x++) {
            let toAppend = new SiteHelperQueryRequestItem2d(
                this._parent.getInfo(),
                this._template,
                this._request.query,
                this._request.context.from + x,
                queryResponse.results[x]
            );
            this.append(toAppend);
            this._results.push(toAppend);
        }
        this._parent.setStatistics(queryResponse.statistics);
        this.resize();
    }
    getOrder() {
        return this._request.context.from;
    }
    getResults() {
        return this._results;
    }
    getResultItem(index: number) {
        return this._results[index];
    }
    isExpired() {
        return this._expired;
    }
    setExpired() {
        this._expired = true;
    }

    isVisible() {
        return !this.containsClass("hide");
    }
    show() {
        this.removeClass("hide");
    }
    hide() {
        this.addClass("hide");
    }
}

class SiteHelperQueryRequestItemInput2d
    extends QueryElement<HTMLInputElement> {

    private _value?: string;
    private readonly _name: string;

    constructor(element: HTMLInputElement, query: string, id: string) {
        super(element);

        var name = this.getAttr("name");
        if (name == null)
            throw "Name not found!";

        this._name = name;

        this.on("focus", () => {
            this._value = this.getVal();
            this.select();
        })
        this.on("blur", async () => {
            let currentValue = this.getVal();
            if (currentValue != null && currentValue != this._value) {
                let data : IQuerySaveRequest = {
                    query: query,
                    id: id,
                    path: this._name,
                    value: currentValue
                }

                let request = new QueryRequest('Api/QueryEdit', {
                    contentType: 'json',
                    method: 'post',
                    data: JSON.stringify(data)
                });
                await request.request();
            }
        });
    }
}

class SiteHelperQueryRequestItem2d
    extends QueryElement<HTMLElement>
    implements ISiteHelperQueryRequestItem {

    private readonly _info: SiteHelperQueryInfo2d;
    private readonly _result: any;
    private readonly _index: number;

    constructor(info: SiteHelperQueryInfo2d, template: QueryTemplate, query: string, index: number, result: any) {
        super(document.createElementFromHTML(template.render(result)));

        this._result = result;
        this._info = info;
        this._index = index;

        this.query("input[type='text']").createAll<SiteHelperQueryRequestItemInput2d, HTMLInputElement>((a) => new SiteHelperQueryRequestItemInput2d(a, query, result.id as string))

        if (this._info.selector == index)
            this.makeSelector();
        if (this._info.active == result.id)
            this.makeActive();
        if (this._info.selected[this._result.id] !== undefined)
            this.makeSelect();
    }

    public makeActive() {
        this._info.active = this._result.id;
        this.addClass("active");
    }
    public makeInactive() {
        delete this._info.active;
        this.removeClass("active");
    }

    public makeSelector() {
        this._info.selector = this._index;
        this.addClass("selector");
    }
    public makeUnselector() {
        delete this._info.selector;
        this.removeClass("selector");
    }

    public isSelected() {
        return this._info.selected[this._result.id] !== undefined;
    }
    public makeSelect() {
        this._info.selected[this._result.id] = this;
        this.addClass("selected");
    }
    public makeUnselect() {
        delete this._info.selected[this._result.id];
        this.removeClass("selected");
    }

    public getResult() {
        return this._result;
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);

        if (this._info.selected[this._result.id] == this)
            this._info.selected[this._result.id] = null;
    }
}

class SiteHelperQueryRequestSpacer2d
    extends QueryElement<HTMLElement> {
    
    private _style: CSSStyleDeclaration;
    private _parent: SiteHelperQuery2d;

    constructor(parent: SiteHelperQuery2d, className: string) {
        super(document.createElement("div"));
        this.addClass("spacer");
        this.addClass(className);

        let style = this.style();
        if (style === undefined)
            throw "Unable to get style!";

        this._style = style;
        this._parent = parent;
    }

    resize(number: number) {
        this._style.width = `${number * this._parent.getTemplateWidth()}px`;
        this._style.flexGrow = number + "";
    }
}